import React from 'react';
import { Grid, Box, Typography, Paper, Chip } from '@mui/material';

export default function ViewMissedMetaLead({ selectedRowData }) {
  const requestPayload = selectedRowData?.request_payload;

  // Parse main request payload
  let payload = {};

  try {
    if (typeof requestPayload === 'string') {
      payload = JSON.parse(requestPayload);
    } else if (
      typeof requestPayload === 'object' &&
      requestPayload !== null
    ) {
      payload = requestPayload;
    }
  } catch (error) {
    console.error('Error parsing request_payload:', error);
  }

  // Parse student_details because it is a JSON string inside payload
  let studentDetails = {};

  try {
    if (typeof payload?.student_details === 'string') {
      studentDetails = JSON.parse(payload.student_details);
    } else if (
      typeof payload?.student_details === 'object' &&
      payload.student_details !== null
    ) {
      studentDetails = payload.student_details;
    }
  } catch (error) {
    console.error('Error parsing student_details:', error);
  }

  // Get first value from arrays
  const getValue = (value) => {
    if (Array.isArray(value)) {
      return value[0] || '-';
    }

    return value || '-';
  };

  // Convert field names to readable labels
  const formatLabel = (key) => {
    return key
      .replace(/\?/g, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Format values
  const formatValue = (key, value) => {
    let formattedValue = getValue(value);

    const valueMap = {
      class_10: 'Class 10',
      class_11: 'Class 11',
      class_12: 'Class 12',
      hyderabad_somajiguda: 'Hyderabad - Somajiguda',
      whatsapp_preferred: 'WhatsApp',
      usa: 'USA',
      uk: 'UK',
      canada: 'Canada',
      australia: 'Australia',
      fall_2028: 'Fall 2028',
      student: 'Student',
    };

    return valueMap[formattedValue] || formattedValue;
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            backgroundColor: '#f8f9fa',
            border: '1px solid #e1e4e8',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              backgroundColor: '#ffffff',
              borderBottom: '1px solid #e1e4e8',
            }}
          >
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 700,
                color: '#24292f',
              }}
            >
              Request Payload
            </Typography>

            <Typography
              sx={{
                fontSize: '12px',
                color: '#6b7280',
                mt: 0.3,
              }}
            >
              Meta Lead Information
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2 }}>

            {/* Campaign */}
            <SectionTitle title="Campaign" />

            <Box
              sx={{
                backgroundColor: '#ffffff',
                border: '1px solid #e1e4e8',
                borderRadius: '6px',
                p: 1.5,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#24292f',
                }}
              >
                {getValue(payload?.campaign_name)}
              </Typography>

              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#6b7280',
                  mt: 0.5,
                }}
              >
                Form: {getValue(payload?.form_name)}
              </Typography>
            </Box>

            {/* Lead Information */}
            <SectionTitle title="Lead Information" />

            <Box
              sx={{
                backgroundColor: '#ffffff',
                border: '1px solid #e1e4e8',
                borderRadius: '6px',
                overflow: 'hidden',
                mb: 2,
              }}
            >
              <InfoRow
                label="Form ID"
                value={getValue(payload?.form_id)}
              />

              <InfoRow
                label="Page ID"
                value={getValue(payload?.page_id)}
              />

              <InfoRow
                label="Meta Lead ID"
                value={getValue(payload?.meta_lead_id)}
                last
              />
            </Box>

            {/* Student Details */}
            <SectionTitle title="Student Details" />

            <Box
              sx={{
                backgroundColor: '#ffffff',
                border: '1px solid #e1e4e8',
                borderRadius: '6px',
                overflow: 'hidden',
              }}
            >
              {Object.entries(studentDetails).map(
                ([key, value], index, array) => {
                  const formattedValue = formatValue(key, value);

                  return (
                    <InfoRow
                      key={key}
                      label={formatLabel(key)}
                      value={formattedValue}
                      last={index === array.length - 1}
                    />
                  );
                }
              )}
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}


/* --------------------------------
   Section Title
-------------------------------- */

function SectionTitle({ title }) {
  return (
    <Typography
      sx={{
        fontSize: '11px',
        fontWeight: 700,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.7px',
        mb: 1,
      }}
    >
      {title}
    </Typography>
  );
}


/* --------------------------------
   Info Row
-------------------------------- */

function InfoRow({ label, value, last = false }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        px: 1.5,
        py: 1.25,
        gap: 2,
        borderBottom: last
          ? 'none'
          : '1px solid #f0f1f2',
      }}
    >
      <Typography
        sx={{
          width: '180px',
          minWidth: '180px',
          fontSize: '12px',
          fontWeight: 600,
          color: '#6b7280',
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: '13px',
          color: '#24292f',
          wordBreak: 'break-word',
          lineHeight: 1.5,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}